import { isBlankValue, shallowJsonStringify } from '../util.js';

export const assertInteger = (val) => {
    if(typeof val !== 'number' || (!Number.isInteger(val)) || (!Number.isFinite(val))) { throw new Error('assertInteger got <'+typeof(val)+'> `'+val+'`'); }
}

export const assertFloat = (val) => {
    if(typeof val !== 'number' || (Number.isNaN(val)) || (!Number.isFinite(val))) { throw new Error('assertFloat got <'+typeof(val)+'> `'+val+'`'); }
}

export const assertId = (val) => {
    if(typeof val !== 'string' || val.length!==24) { throw new Error('assertId got <'+typeof(val)+'> `'+val+'`'); }
}

export const assertIdOrInteger = (val) => {
    if(typeof val == 'string' && val.length===24) { return; }
    if(typeof val !== 'number' || (!Number.isInteger(val)) || (!Number.isFinite(val))) { throw new Error('assertIdOrInteger got <'+typeof(val)+'> `'+val+'`'); }
}

export const assertString = (val) => {
    if(typeof val !== 'string') { throw new Error('assertString got <'+typeof(val)+'> `'+val+'`'); }
}

export const assertNonemptyString = (val) => {
    if(typeof val !== 'string' || val.length === 0) { throw new Error('assertNonemptyString got <'+typeof(val)+'> `'+val+'`'); }
}

export const assert = (val, message) => {
    if(!val) { throw new Error('Assertion failed: '+(message ?? '')); }
}

export const assertEquals = (val1, val2, message) => {
    if(val1 !== val2) { throw new Error('Equal Assertion failed: '+(message ?? '')+' (<'+typeof(val1)+'> `'+val1+'` did not equal <'+typeof(val2)+'> `'+val2+'`)'); }
}

export const assertNotEquals = (val1, val2, message) => {
    if(val1 === val2) { throw new Error('NotEqual Assertion failed: '+(message ?? '')+' (<'+typeof(val1)+'> `'+val1+'` did equal <'+typeof(val2)+'> `'+val2+'`)'); }
}

export const assertBoolean = (val, message) => {
    if(typeof val !== 'boolean') { throw new Error('Boolean type assertion failed: '+(message ?? '')+' (`'+shallowJsonStringify(val)+'`)'); }
}

export const assertArray = (val, message) => {
    if(!Array.isArray(val)) { throw new Error('Array type assertion failed: '+(message ?? '')+' (`'+shallowJsonStringify(val)+'`)'); }
}

export const assertNonemptyArray = (val) => {
    if(!Array.isArray(val) || val.length === 0) { throw new Error('assertNonemptyArray got <'+typeof(val)+'> `'+val+'`'); }
}

export const assertIn = (key, obj) => {
    assertObject(obj);
    if(!obj.hasOwnProperty(key)) { throw new Error('Key <'+typeof(key)+'> `'+key+'` was not a property of <'+typeof(obj)+'> `'+shallowJsonStringify(obj)+'`'); }
}

export const assertInMap = (key, map) => {
    assertMap(map);
    if(!map.has(key)) { throw new Error('Key <'+typeof(key)+'> `'+key+'` was not a key in <'+typeof(map)+'> `'+shallowJsonStringify(map)+'`'); }
}

export const assertNotIn = (key, obj) => {
    assertObject(obj);
    if(obj.hasOwnProperty(key)) { throw new Error('Key <'+typeof(key)+'> `'+key+'` was a property of <'+typeof(obj)+'> `'+shallowJsonStringify(obj)+'`'); }
}

export const assertIncludes = (array, value) => {
    assertNonemptyArray(array);
    if(!(array.includes(value))) { throw new Error('Value <'+typeof(value)+'> `'+value+'` was not included in <'+typeof(array)+'> `'+shallowJsonStringify(array)+'`'); }
}

export const assertNotIncludes = (array, value) => {
    assertArray(array);
    if(array.includes(value)) { throw new Error('Value <'+typeof(value)+'> `'+value+'` was included in <'+typeof(array)+'> `'+shallowJsonStringify(array)+'`'); }
}

export const assertHas = (set, value) => {
    if(!(set.has(value))) { throw new Error('Value <'+typeof(value)+'> `'+value+'` was not had in <'+typeof(set)+'> `'+shallowJsonStringify(set)+'`'); }
}

export const assertObject = (val) => {
    if(typeof val !== 'object' || val === null || Array.isArray(val)) { throw new Error('assertObject got <'+typeof(val)+'> `'+val+'`'); }
}

export const assertMap = (val) => {
    if(!(val instanceof Map)) { throw new Error('assertMap got <'+typeof(val)+'> `'+val+'`'); }
}

export const assertNonemptyObject = (val) => {
    if(typeof val !== 'object' || val === null || Array.isArray(val) || Object.keys(val).length === 0) { throw new Error('assertNonemptyObject got <'+typeof(val)+'> `'+val+'`'); }
}

export const assertEmptyObject = (val) => {
    if(typeof val !== 'object' || val === null || Array.isArray(val) || Object.keys(val).length !== 0) { throw new Error('assertEmptyObject got <'+typeof(val)+'> `'+val+'`'); }
}

export const assertNonblank = (val) => {
    if(isBlankValue(val)) { throw new Error('assertNonblank got <'+typeof(val)+'> `'+val+'`'); }
}

export const assertNode = (val) => {
    if(!(val instanceof Node)) { throw new Error('assertNode got <'+typeof(val)+'> `'+val+'`'); }
}

export const isIntStrict = (value) => {
    const parsed_int = parseInt(value);
    return (parsed_int.toString() === value.toString());
}

export const parseIntStrict = (value) => {
    const parsed_int = parseInt(value);
    if(parsed_int.toString() !== value.toString()) { throw new Error('Invalid integer `'+value+'`'); }
    return parsed_int;
}

export const tryParseIntStrict = (value) => {
    const parsed_int = parseInt(value);
    return ((parsed_int.toString() === value.toString()) ? parsed_int : value);
}

export const isFloatStrict = (value) => {
    if(typeof value !== 'number' && typeof value !== 'string') { return false; }
    if(typeof value === 'string' && value.trim() === '') { return false; }
    const parsed_float = Number(value);
    return !Number.isNaN(parsed_float);
}

export const parseFloatStrict = (value) => {
    if(typeof value !== 'number' && typeof value !== 'string') { throw new Error('Invalid float `'+value+'`'); }
    if(typeof value === 'string' && value.trim() === '') { throw new Error('Invalid float `'+value+'`'); }
    const parsed_float = Number(value);
    if(Number.isNaN(parsed_float)) { throw new Error('Invalid float `'+value+'`'); }
    return parsed_float;
}

export const tryParseFloatStrict = (value) => {
    if(typeof value !== 'number' && typeof value !== 'string') { return value; }
    if(typeof value === 'string' && value.trim() === '') { return value; }
    const parsed_float = Number(value);
    if(Number.isNaN(parsed_float)) { return value; }
    return parsed_float;
}